import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import ApiURL from "../../controllers/Api";
import logo from "../../images/logo.png";

/** ------------------ Toast (no external library) ------------------ */
function ToastStack({ toasts, onRemove }) {
  const stackRef = useRef(null);

  useEffect(() => {
    if (!stackRef.current) return;
    gsap.fromTo(
      stackRef.current.querySelectorAll(".toast-item"),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, ease: "power2.out" }
    );
  }, [toasts]);

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div ref={stackRef} className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-[#d4af37]/25 bg-[#fff8e8] text-[#6a4b1f]";

          return (
            <div
              key={t.id}
              className={[
                "toast-item pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border border-black/10 bg-white/70 px-2 py-1 text-xs font-semibold transition hover:bg-white"
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

async function safeJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, message: "Invalid JSON", raw: text };
  }
}

/** Parse products string */
function parseProductsString(productsStr) {
  if (!productsStr) return [];
  return productsStr
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((prod) => {
      const parts = prod.split("|");
      const [
        productId,
        productName,
        description,
        imgPath,
        quantity,
        price,
        size,
        totalAmount,
        igst,
        sgst,
        cgst,
        grandTotal,
        roundedAmount,
        roundingDifference,
      ] = parts;

      return {
        productId: productId || "",
        productName: productName || "",
        description: description || "",
        imgPath: imgPath || "",
        quantity: Number(quantity || 0),
        price: Number(price || 0),
        size: size || "",
        totalAmount: Number(totalAmount || 0),
        igst: Number(igst || 0),
        sgst: Number(sgst || 0),
        cgst: Number(cgst || 0),
        grandTotal: Number(grandTotal || 0),
        roundedAmount: Number(roundedAmount || 0),
        roundingDifference: Number(roundingDifference || 0),
      };
    });
}

function calcOrderTotals(items) {
  const grand = items.reduce((s, it) => s + (Number(it.grandTotal) || 0), 0);
  const rounded = Math.round(grand);
  const roundingDiff = (grand - rounded) * -1;
  return { grand, rounded, roundingDiff };
}

/** CSV helpers */
function downloadCsv(filename, rows) {
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          const v = cell == null ? "" : String(cell);
          const escaped = v.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const DetailedReport = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [customerNameFilter, setCustomerNameFilter] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);

  // Pagination (BY ORDER)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // orders per page

  // Toasts
  const [toasts, setToasts] = useState([]);

  const wrapRef = useRef(null);
  const filterRef = useRef(null);
  const tableContainerRef = useRef(null);

  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((p) => [...p, { id, type, message, title }]);
    window.setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 2800);
  };
  const removeToast = (id) => setToasts((p) => p.filter((t) => t.id !== id));

  // Horizontal scroll functions (works for mobile + desktop)
  const scrollTableLeft = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: -320, behavior: "smooth" });
    }
  };

  const scrollTableRight = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({ left: 320, behavior: "smooth" });
    }
  };

  // GSAP page entrance
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".dr-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);
    return () => ctx.revert();
  }, []);

  // close filters on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (!filterRef.current) return;
      if (filterRef.current.contains(e.target)) return;
      setFilterVisible(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${ApiURL}/getReportData.php`);
      const data = await safeJson(res);

      if (data?.success) {
        const incoming = data?.data?.recent_orders || data?.data?.orders || [];
        setAllOrders(Array.isArray(incoming) ? incoming : []);
      } else {
        setAllOrders([]);
        toast("error", data?.message || "Failed to fetch order details.");
      }
    } catch (err) {
      console.error(err);
      setAllOrders([]);
      toast("error", "An error occurred while fetching order details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Normalize orders with parsed products + totals
  const normalizedOrders = useMemo(() => {
    return (allOrders || []).map((o) => {
      const items = parseProductsString(o.products);
      const totals = calcOrderTotals(items);
      return {
        ...o,
        items,
        totals,
        orderDateObj: o.order_date ? new Date(o.order_date) : null,
      };
    });
  }, [allOrders]);

  // Derived filtered list (non-destructive)
  const filteredOrders = useMemo(() => {
    let list = [...normalizedOrders];

    if (statusFilter) {
      list = list.filter(
        (o) => String(o.status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((o) => o.orderDateObj && o.orderDateObj >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((o) => o.orderDateObj && o.orderDateObj <= to);
    }

    if (customerNameFilter.trim()) {
      const q = customerNameFilter.trim().toLowerCase();
      list = list.filter((o) => String(o.full_name || "").toLowerCase().includes(q));
    }

    return list;
  }, [normalizedOrders, statusFilter, dateFrom, dateTo, customerNameFilter]);

  // Pagination derived values
  const totalOrders = filteredOrders.length;

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalOrders / pageSize));
  }, [totalOrders, pageSize]);

  const pagedOrders = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredOrders.slice(start, end);
  }, [filteredOrders, page, pageSize]);

  // reset page on filter / page size change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, dateFrom, dateTo, customerNameFilter, pageSize]);

  // clamp page if data shrinks
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const resetFilters = () => {
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setCustomerNameFilter("");
    toast("info", "Filters reset.");
  };

  const applyFilters = () => {
    setFilterVisible(false);
    toast("success", "Filters applied.");
  };

  // CSV export (detailed rows) - exports ALL FILTERED ORDERS (not only current page)
  const exportToCSV = () => {
    const header = [
      "SNo",
      "Order ID",
      "Customer Name",
      "Company Name",
      "Order Date",
      "Status",
      "Product Name",
      "Quantity",
      "Size",
      "Price",
      "Line Total",
      "IGST",
      "SGST",
      "CGST",
      "Line Grand Total",
      "Order Grand Amount",
      "Order Rounding Difference",
      "Order Payable Amount",
    ];

    const rows = [];

    filteredOrders.forEach((order, orderIndex) => {
      const items = order.items || [];
      const orderGrand = order.totals.grand;
      const orderRounded = order.totals.rounded;
      const roundDiff = order.totals.roundingDiff;

      items.forEach((it, itIdx) => {
        const sno = `${orderIndex + 1}.${itIdx + 1}`;

        rows.push([
          sno,
          order.order_id,
          order.full_name,
          order.address_line1,
          order.order_date ? new Date(order.order_date).toLocaleDateString() : "",
          order.status,
          it.productName,
          it.quantity,
          it.size,
          Number(it.price || 0).toFixed(2),
          Number(it.totalAmount || 0).toFixed(2),
          Number(it.igst || 0).toFixed(2),
          Number(it.sgst || 0).toFixed(2),
          Number(it.cgst || 0).toFixed(2),
          Number(it.grandTotal || 0).toFixed(2),
          itIdx === items.length - 1 ? orderGrand.toFixed(2) : "",
          itIdx === items.length - 1 ? roundDiff.toFixed(2) : "",
          itIdx === items.length - 1 ? orderRounded.toFixed(2) : "",
        ]);
      });
    });

    downloadCsv("order_details.csv", [header, ...rows]);
    toast("success", "Detailed report CSV downloaded.");
  };

  if (loading) {
    return <div className="min-h-screen bg-[#F7EFE7] p-6 font-[Kanit] text-[#6f5e57]">Loading orders...</div>;
  }

  if (!filteredOrders || filteredOrders.length === 0) {
    return (
      <div className="min-h-screen bg-[#F7EFE7] p-4 font-[Kanit] sm:p-6">
        <ToastStack toasts={toasts} onRemove={removeToast} />
        <div className="rounded-[24px] border border-[#d8c6b7] bg-[#fffaf5] p-6 shadow-[0_20px_55px_rgba(43,0,0,0.08)]">
          <div className="font-serif text-2xl font-medium text-[#2B0000]">Detailed Report</div>
          <div className="mt-2 text-sm text-[#6f5e57]">No orders match the current filters.</div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-xl border border-[#d6c4b5] bg-[#f1e6dc] px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#e8d8ca]"
            >
              Reset Filters
            </button>
            <button
              type="button"
              onClick={fetchOrders}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="detailed-report-luxury min-h-screen w-full overflow-x-hidden bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <div className="dr-anim mx-auto max-w-[1700px] rounded-[26px] border border-[#d8c6b7] bg-[#fffaf5] p-4 shadow-[0_22px_60px_rgba(43,0,0,0.08)] sm:p-6 lg:p-8">
        {/* Header */}
        <div className="dr-anim flex flex-col gap-5 border-b border-[#e3d6cb] pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="logo" className="h-12 w-12 rounded-xl border border-[#d4af37]/30 bg-white object-contain p-1 shadow-sm" />
            <div>
              <div className="font-serif text-2xl font-medium text-[#2B0000] sm:text-3xl">Detailed Report</div>
              <div className="text-sm text-[#6f5e57]">Order-level itemized reporting</div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={exportToCSV}
              className="rounded-xl bg-[#8f6f38] px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-[#765a2d]"
            >
              Export CSV
            </button>

            <button
              type="button"
              onClick={() => setFilterVisible((v) => !v)}
              className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
            >
              Filters
            </button>

            <button
              type="button"
              onClick={fetchOrders}
              className="rounded-xl border border-[#d6c4b5] bg-white px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {filterVisible && (
          <div ref={filterRef} className="dr-anim mt-5 rounded-[22px] border border-[#d8c6b7] bg-[linear-gradient(145deg,#fffaf5,#f3e7dc)] p-4 shadow-[0_16px_40px_rgba(43,0,0,0.06)] sm:p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Order Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                >
                  <option value="">All</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Date From</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Date To</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#4a352f]">Customer Name</label>
                <input
                  type="text"
                  value={customerNameFilter}
                  onChange={(e) => setCustomerNameFilter(e.target.value)}
                  placeholder="Search by name"
                  className="mt-2 w-full rounded-xl border border-[#d6c4b5] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-xl border border-[#d6c4b5] bg-[#f1e6dc] px-4 py-2.5 text-sm font-semibold text-[#2B0000] transition hover:bg-[#e8d8ca]"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="rounded-xl bg-[#2B0000] px-4 py-2.5 text-sm font-semibold text-[#f4dfae] shadow-md transition hover:bg-[#4A090B]"
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {/* Horizontal scroll controls (now visible on mobile too) */}
        <div className="dr-anim mt-6 flex items-center justify-between gap-3 rounded-[18px] border border-[#e2d4c8] bg-[#fbf3eb] p-3">
          <button
            type="button"
            onClick={scrollTableLeft}
            className="flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition-all duration-200 hover:bg-[#fff7ed] active:translate-y-[1px]"
            aria-label="Scroll table left"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="ml-2 hidden sm:inline">Scroll Left</span>
          </button>

          <div className="text-center text-xs font-medium text-[#75645d] sm:text-sm">
            Swipe or use buttons to scroll horizontally
          </div>

          <button
            type="button"
            onClick={scrollTableRight}
            className="flex items-center justify-center rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition-all duration-200 hover:bg-[#fff7ed] active:translate-y-[1px]"
            aria-label="Scroll table right"
          >
            <span className="mr-2 hidden sm:inline">Scroll Right</span>
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Pagination */}
        <div className="dr-anim mt-4 flex flex-col gap-3 rounded-[18px] border border-[#e2d4c8] bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-[#5d4b44]">
            Showing{" "}
            <span className="font-semibold">
              {totalOrders === 0 ? 0 : (page - 1) * pageSize + 1}
            </span>
            {" - "}
            <span className="font-semibold">{Math.min(page * pageSize, totalOrders)}</span>{" "}
            of <span className="font-semibold">{totalOrders}</span> orders
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
              aria-label="Orders per page"
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>

            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className={[
                "rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition",
                page === 1 ? "cursor-not-allowed opacity-50" : "hover:bg-[#fff7ed]",
              ].join(" ")}
            >
              Prev
            </button>

            <div className="px-2 text-sm text-[#5d4b44]">
              Page <span className="font-semibold">{page}</span> /{" "}
              <span className="font-semibold">{totalPages}</span>
            </div>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={[
                "rounded-xl border border-[#d6c4b5] bg-white px-3 py-2 text-sm font-semibold text-[#2B0000] transition",
                page === totalPages ? "cursor-not-allowed opacity-50" : "hover:bg-[#fff7ed]",
              ].join(" ")}
            >
              Next
            </button>
          </div>
        </div>

        {/* Table (VISIBLE on mobile + desktop) */}
        <div className="dr-anim mt-3">
          <div
            ref={tableContainerRef}
            className="overflow-x-auto rounded-[22px] border border-[#d8c6b7] bg-white shadow-[0_16px_42px_rgba(43,0,0,0.06)] scroll-smooth overscroll-x-contain"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#8f6f38 #f4e8dd",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-x",
            }}
          >
            <table className="min-w-[1500px] w-full text-left text-sm">
              <thead className="sticky top-0 bg-[#eee2d7] text-xs font-semibold uppercase tracking-[0.12em] text-[#4b352f]">
                <tr>
                  <th className="px-4 py-3">SNo</th>
                  <th className="px-4 py-3">Order ID</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Order Date</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3 text-center">Qty</th>
                  <th className="px-4 py-3 text-center">Size</th>
                  <th className="px-4 py-3 text-right">Price</th>
                  <th className="px-4 py-3 text-right">Line Total</th>
                  <th className="px-4 py-3 text-right">IGST</th>
                  <th className="px-4 py-3 text-right">SGST</th>
                  <th className="px-4 py-3 text-right">CGST</th>
                  <th className="px-4 py-3 text-right">Line Grand</th>
                  <th className="px-4 py-3 text-right font-bold">Order Grand</th>
                  <th className="px-4 py-3 text-right font-bold">Round Diff</th>
                  <th className="px-4 py-3 text-right font-bold">Payable</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#eadfd5]">
                {pagedOrders.map((order, orderIndex) => {
                  const items = order.items || [];
                  const orderGrand = order.totals.grand;
                  const orderRounded = order.totals.rounded;
                  const roundDiff = order.totals.roundingDiff;

                  return items.map((it, itIdx) => (
                    <tr
                      key={`${order.order_id}-${itIdx}`}
                      className={[
                        "dr-report-row transition hover:bg-[#fff5ea]",
                        itIdx === items.length - 1 ? "dr-report-order-end" : "",
                      ].join(" ")}
                    >
                      <td className="px-4 py-3">
                        {`${(page - 1) * pageSize + (orderIndex + 1)}.${itIdx + 1}`}
                      </td>

                      {itIdx === 0 && (
                        <>
                          <td rowSpan={items.length} className="px-4 py-3 align-top font-semibold text-[#2B0000]">
                            {order.order_id}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top">
                            <div className="font-semibold text-[#2B0000]">{order.full_name}</div>
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top text-[#5d4b44]">
                            {order.address_line1}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top text-[#5d4b44]">
                            {order.order_date ? new Date(order.order_date).toLocaleDateString() : ""}
                          </td>
                          <td rowSpan={items.length} className="px-4 py-3 align-top">
                            <span
                              className={[
                                "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold",
                                String(order.status).toLowerCase() === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : String(order.status).toLowerCase() === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800",
                              ].join(" ")}
                            >
                              {order.status}
                            </span>
                          </td>
                        </>
                      )}

                      <td className="px-4 py-3 text-[#2B0000]">{it.productName}</td>
                      <td className="px-4 py-3 text-center text-[#2B0000]">{it.quantity}</td>
                      <td className="px-4 py-3 text-center text-[#5d4b44]">{it.size}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">₹{Number(it.price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">₹{Number(it.totalAmount || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">₹{Number(it.igst || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">₹{Number(it.sgst || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">₹{Number(it.cgst || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-[#2B0000]">₹{Number(it.grandTotal || 0).toFixed(2)}</td>

                      {itIdx === items.length - 1 && (
                        <>
                          <td className="px-4 py-3 text-right font-bold text-[#2B0000]">₹{orderGrand.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-bold text-[#2B0000]">₹{roundDiff.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-bold text-[#2B0000]">₹{orderRounded.toFixed(2)}</td>
                        </>
                      )}

                      {itIdx !== items.length - 1 && (
                        <>
                          <td className="px-4 py-3" />
                          <td className="px-4 py-3" />
                          <td className="px-4 py-3" />
                        </>
                      )}
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>

          {/* Optional helper text on very small screens */}
          <div className="mt-2 text-xs text-[#7d6c64] sm:hidden">
            Tip: drag the table left/right to view all columns.
          </div>
        </div>
      </div>

      <style>{`
        .detailed-report-luxury {
          background:
            radial-gradient(circle at 6% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 92%, rgba(43,0,0,.04), transparent 30%),
            #F7EFE7;
        }

        .detailed-report-luxury input,
        .detailed-report-luxury select,
        .detailed-report-luxury button {
          font: inherit;
        }

        .detailed-report-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .detailed-report-luxury th {
          border-bottom: 1px solid #d8c6b7;
        }

        .detailed-report-luxury .dr-report-row > td {
          border-bottom: 1px solid #e2d4c8;
        }

        .detailed-report-luxury .dr-report-order-end > td {
          border-bottom: 2px solid #c9a872;
        }

        .detailed-report-luxury ::selection {
          color: #fff7e7;
          background: #7a3131;
        }

        .detailed-report-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(143,111,56,.55) rgba(212,175,55,.07);
        }

        .detailed-report-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .detailed-report-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .detailed-report-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(143,111,56,.55);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .detailed-report-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .detailed-report-luxury *,
          .detailed-report-luxury *::before,
          .detailed-report-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DetailedReport;
